// frontend/src/components/shop/PunchCard.js
// Shop V1 — Visual punch card tracker for the Random Card Printer.
// Pure display component — receives punchCount (0–4) as prop.
// 5th punch awards 1 free pull credit (handled by shopApi, not this component).

import React, { useEffect, useRef, useState } from 'react';
import './PunchCard.css';

const TOTAL_SLOTS = 5;

/**
 * PunchCard
 *
 * Renders 5 punch slots. Filled slots show a "punched hole" visual.
 * When punchCount increases, the newly filled slot gets the --new modifier
 * for the shopPunchFill pop animation, then the modifier is removed.
 *
 * @param {object} props
 * @param {number} props.punchCount — 0 to 4 (5 resets to 0 and grants free pull)
 */
function PunchCard({ punchCount }) {
    const prevCountRef = useRef(punchCount);
    const [newSlotIndex, setNewSlotIndex] = useState(null);

    // Detect when punchCount increases to trigger the pop animation on the new slot
    useEffect(() => {
        const prev = prevCountRef.current;
        if (punchCount > prev) {
            // The newly filled slot is at index (punchCount - 1)
            setNewSlotIndex(punchCount - 1);
            // Remove the --new modifier after the animation completes (300ms)
            const timer = setTimeout(() => setNewSlotIndex(null), 400);
            prevCountRef.current = punchCount;
            return () => clearTimeout(timer);
        }
        prevCountRef.current = punchCount;
    }, [punchCount]);

    const clampedCount = Math.max(0, Math.min(TOTAL_SLOTS - 1, punchCount));

    return (
        <div className="punch-card" aria-label={`Punch card: ${clampedCount} of ${TOTAL_SLOTS - 1} punches`}>
            <span className="punch-card-label">Punch Card</span>
            <div className="punch-card-slots">
                {Array.from({ length: TOTAL_SLOTS }, (_, i) => {
                    const isFilled = i < clampedCount;
                    const isNew = isFilled && i === newSlotIndex;
                    const slotClass = [
                        'punch-slot',
                        isFilled ? 'punch-slot--filled' : '',
                        isNew ? 'punch-slot--new' : '',
                    ].filter(Boolean).join(' ');

                    return (
                        <span
                            key={i}
                            className={slotClass}
                            aria-label={isFilled ? 'Punched' : 'Empty'}
                        />
                    );
                })}
            </div>
            <span className="punch-card-hint">
                {clampedCount}/{TOTAL_SLOTS - 1} — 5 duplicates = 1 free pull
            </span>
        </div>
    );
}

export default PunchCard;
